import React from 'react';
import PropTypes from 'prop-types';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { event } from '@uhg-abyss/web/tools/event';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Link } from '@uhg-abyss/web/ui/Link';
import { config } from '@uhg-abyss/web/tools/config';
import { getFeature } from '../../../../../redux/tools/features';

const InfoLabel = styled('label', {
  fontSize: '$md',
  color: '$error1',
});

const CustomLink = styled(Link, {
  display: 'inline-flex',
});

const MarginDiv = styled('div', {
  margin: '5px 0px 8px 0px',
});

const StyledLabel = styled('label', {
  fontSize: '$md',
  color: '$gray8',
});

export const TaxIdNumbers = ({
  selectedProvider,
  setSelectedProvider,
  organizations,
  isTaxIdNumbersLoading,
}) => {
  const { events } = usePropsContext();
  const { corporateProviderMpin, taxIdNumber } = selectedProvider;

  const handleChange = (selectedTaxId) => {
    if (selectedProvider?.taxIdNumber !== selectedTaxId) {
      const orgTinMatch = (organizations || []).find(
        (org) => org.tin === selectedTaxId
      );

      setSelectedProvider((prevProvider) => {
        if (prevProvider.taxIdNumber === selectedTaxId) {
          return prevProvider;
        }
        return {
          ...selectedProvider,
          organizationId: orgTinMatch.id || '',
          organizationName: orgTinMatch.name || '',
          taxIdNumber: orgTinMatch.tin,
          tinType: orgTinMatch.tinType,
          allPermission: orgTinMatch.allPermission || false,
          permissionCode: orgTinMatch.permissionCode || [],
          permissionSubCode: orgTinMatch.permissionSubCode || [],
          privileges: orgTinMatch.privileges || [],
        };
      });
      events.onTaxIdNumberChange(orgTinMatch);
    }
  };

  return (
    <MarginDiv>
      <StyledLabel
        id='tax-id-header'
        data-testid='tax-id-header'
        htmlFor='provider-select-taxidnumbers'
        data-uhc-brand='font--semibold'
      >
        Tax ID Number
      </StyledLabel>
      <If condition={getFeature('DIGITAL_SECURITY_APIS')}>
        &nbsp;
        <InfoLabel
          id='tax-id-header-missing-tin'
          data-testid='tax-id-header-missing-tin'
          htmlFor='provider-select-taxidnumbers'
          data-uhc-brand='font--semibold'
          color='red'
        >
          Missing a TIN?
        </InfoLabel>
        &nbsp;
        <CustomLink
          onClick={() => {
            event('EXIT_LINK_NAVIGATE', {
              linkText: 'Missing a TIN? Click Here',
            });
            window.open(config('DIGITAL_SECURITY_URL'));
          }}
        >
          Click Here
        </CustomLink>
      </If>
      <SelectInput
        id='provider-select-taxidnumbers'
        data-testid='provider-select-taxidnumbers'
        aria-label='change tax ID number'
        aria-labelledby='tax-id-header'
        aria-required='true'
        enableOutsideScroll
        isLoading={isTaxIdNumbersLoading}
        value={taxIdNumber}
        options={
          organizations.length > 0 ? organizations : [{ tin: taxIdNumber }]
        }
        placeholder='Select Tax ID Number...'
        labelKey='tin'
        valueKey='tin'
        isDisabled={!corporateProviderMpin || organizations.length === 0}
        onChange={handleChange}
        validators={{ required: (value) => !value }}
      />
    </MarginDiv>
  );
};

TaxIdNumbers.propTypes = {
  selectedProvider: PropTypes.shape({
    organizationId: PropTypes.string,
    corporateProviderMpin: PropTypes.string,
    taxIdNumber: PropTypes.string,
    uhcProviderId: PropTypes.string,
    userId: PropTypes.string,
    uniqueId: PropTypes.string,
  }),
  setSelectedProvider: PropTypes.func.isRequired,
  organizations: PropTypes.shape(PropTypes.object),
  isTaxIdNumbersLoading: PropTypes.bool,
};

TaxIdNumbers.defaultProps = {
  selectedProvider: null,
  organizations: null,
  isTaxIdNumbersLoading: false,
};
